/**
 * @fileoverview Personal investments component for displaying a person's investment history
 * @active This component is actively used in the person details view
 *
 * Displays a table of personal investments made by an individual, including
 * company information, investment rounds, dates and amounts.
 *
 * @see {@link src/components/personBy_Id/overview/Overview.tsx} - Parent component
 * @see {@link src/components/ui/tables/TableRender.tsx} - Table component used for display
 * @see {@link src/utils/formatDate.ts} - Date formatting utilities
 * @see {@link src/utils/formatNumber.ts} - Number formatting utilities for currency formatting
 * @see {@link src/components/personBy_Id/overview/AssociatedCompanies/AssociatedCompanies.tsx} - Similar component for styling reference
 * @see {@link src/types/joins/companyFundraisingInvestors.ts} - Type definitions for personal investments
 */

"use client";

import { Box } from "@chakra-ui/react";
import type {
  PersonalInvestment,
  PersonWithInvestments,
} from "@/types/joins/companyFundraisingInvestors";
import { BlueTextBoxArrow } from "@/components/ui/box/BlueTextBoxArrow";
import { PersonalInvestmentsModal } from "./PersonalInvestmentsModal";
import { TableRender } from "@/components/ui/tables/TableRender";
import { formatNumber } from "@/utils/formatNumber";
import { imgSelector } from "@/utils/imgSelector";
import {
  CompanyElement,
  PlainTextInTable,
} from "../AssociatedCompanies/AssociatedCompanies";
import { formatDateTimeFromDB, formatValues } from "@/utils/formatDate";
import { PersonOverviewWrapper } from "../Overview";

/**
 * Props interface for the PersonalInvestments component
 * @interface IPersonalInvestmentsProps
 */
interface IPersonalInvestmentsProps {
  /** Person data containing investment history */
  onePerson: PersonWithInvestments;
}

/**
 * Temporary mock data for testing styling when no real investments exist
 * Only used in non-production environments for testing and staging
 * @constant {Array<PersonalInvestment>} MOCK_INVESTMENT
 */
const MOCK_INVESTMENT: PersonalInvestment[] = [
  {
    company: {
      nameBrand: "Example Startup",
      slug: "example-startup",
      img: "https://example.com/logo.png",
    },
    round: "Series A",
    date: "2024-03-20",
    amount: 1000000,
  },
];

/**
 * Determines if mock data should be shown based on environment variables
 * Mock data will only be shown when:
 * 1. NEXT_PUBLIC_ENV is not 'production' AND
 * 2. NODE_ENV is not 'development'
 * 
 * @returns {boolean} Whether mock data should be used
 */
const shouldShowMockData = (): boolean => {
  const publicEnv = process.env.NEXT_PUBLIC_ENV;
  const nodeEnv = process.env.NODE_ENV;
  
  return publicEnv !== 'production' && nodeEnv !== 'development';
};

/**
 * PersonalInvestments component that displays a person's investment history
 *
 * This component renders a table of investments made by a person, including
 * the company, investment round, date, and amount information.
 *
 * @param {IPersonalInvestmentsProps} props - Component props
 * @param {Object} props.onePerson - Person data containing investment information
 * @returns {JSX.Element} Rendered component
 */
function PersonalInvestments({
  onePerson,
}: IPersonalInvestmentsProps): JSX.Element {
  /**
   * Column definitions for the investments table
   */
  const personalInvestmentsMask = [
    { title: "Company" },
    { title: "Round" },
    { title: "Date" },
    { title: "Amount" },
  ];

  /**
   * Renders different block types based on the column type
   * @param {Object} params - Block switch parameters
   * @param {Object} params.item - Column definition with title property
   * @param {PersonalInvestment} params.mainItem - Investment row data
   * @returns {JSX.Element | null} Rendered block component
   */
  const blockSwitch = ({
    item,
    mainItem,
  }: {
    item: { title: string };
    mainItem: PersonalInvestment;
  }): JSX.Element | null => {
    switch (item?.title) {
      case "Company": {
        const nameBrand = mainItem?.company?.nameBrand || "Unnamed Company";
        const img = mainItem?.company?.img || "";
        const link = `/companies/${mainItem?.company?.slug || ""}`;
        return (
          <CompanyElement
            nameBrand={nameBrand}
            img={img}
            imgType="person"
            link={link}
            isBlurred={true}
          />
        );
      }
      case "Round": {
        const data = mainItem?.round;
        return <PlainTextInTable data={data} styleText={{}} isNull={!data} />;
      }
      case "Date": {
        const date = mainItem?.date || "";
        const data = date ? formatDateTimeFromDB(date, formatValues.short) : "";
        return <PlainTextInTable data={data} styleText={{}} isNull={!data} />;
      }
      case "Amount": {
        // Use formatNumber to properly format currency values
        const amount = mainItem?.amount;
        const data = amount !== undefined ? formatNumber(amount) : undefined;
        return (
          <PlainTextInTable
            data={data}
            styleText={{}}
            isNull={data === undefined}
          />
        );
      }
      default:
        return null;
    }
  };

  // Use mock data if no real investments exist and environment conditions are met
  const displayData = onePerson?.investments?.length
    ? onePerson.investments
    : (shouldShowMockData() ? MOCK_INVESTMENT : []);

  return (
    <PersonOverviewWrapper
      customStyleInner={{ maxW: { base: "100%", md: "100%", xl: "1124px" } }}
    >
      <BlueTextBoxArrow
        title="Personal Investments"
        heading="h3"
        headingStyles={{}}
        boxStyles={{}}
      />
      <TableRender
        mask={personalInvestmentsMask}
        data={displayData}
        customStyleOuter={{ px: "20px", mt: "20px" }}
        blockSwitch={blockSwitch}
        ModalComponent={PersonalInvestmentsModal}
        history={[]}
      />
    </PersonOverviewWrapper>
  );
}

export default PersonalInvestments;
